import {
  Dialog,
  exportPath,
  getBookPreview,
  getCalendarFeed,
  getFamily,
  getPlan,
  getTableRows,
  renewCalendarFeed,
  type BookPreview,
  type ExportChoices,
  type Kin,
  type Plan,
  type TableRows,
} from '@trippath/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BookPages,
  Check,
  Choose,
  FeedField,
  Group,
  RowsPreview,
  sizeSaid,
} from './ExportPanels';

type Tab = 'pdf' | 'gpx' | 'geo' | 'table' | 'ics';

const tabs: readonly Tab[] = ['pdf', 'gpx', 'geo', 'table', 'ics'];

interface Props {
  trip: string;
  open: boolean;
  onClose: () => void;
}

/** A trip's name as its file's, as the server names it: `Aasia 2027` is `aasia-2027`. */
function fileName(name: string): string {
  return name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
}

/**
 * The trip taken elsewhere (the Export mockup): the trip book as a PDF the server makes, the
 * route for a map app as GPX, KML, KMZ or GeoJSON, the money and the days as tables, and the
 * calendar's own link. Each file holds what is ticked, and is downloaded as it is.
 */
export function ExportDialog({ trip, open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<Tab>('pdf');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [family, setFamily] = useState<Kin[]>([]);
  const [failed, setFailed] = useState(false);
  const [book, setBook] = useState({
    cover: true,
    days: true,
    bookings: true,
    entry: true,
    money: false,
  });
  const [lang, setLang] = useState<'et' | 'en'>(i18n.language.startsWith('et') ? 'et' : 'en');
  const [paper, setPaper] = useState<'a4' | 'letter'>('a4');
  const [daily, setDaily] = useState(true);
  const [branches, setBranches] = useState(false);
  // The book as it came out, with the choices it was made of: shown while they still hold.
  const [made, setMade] = useState<{ of: string; book: BookPreview } | null>(null);
  const [gpx, setGpx] = useState({ stops: true, ways: true, flights: false, sights: true });
  const [geoKind, setGeoKind] = useState<'kml' | 'kmz' | 'geojson'>('kml');
  const [geo, setGeo] = useState({ places: true, lines: true, costs: false });
  const [tables, setTables] = useState({ costs: true, bookings: true, days: false });
  const [tableKind, setTableKind] = useState<'xlsx' | 'csv'>('xlsx');
  const [rows, setRows] = useState<TableRows[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let live = true;
    Promise.all([getPlan(trip), getFamily(trip)]).then(
      ([read, kin]) => {
        if (!live) return;
        setPlan(read);
        setFamily(kin.trips);
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [open, trip]);

  const bookChoices: ExportChoices = useMemo(
    () => ({ ...book, lang, paper, daily, branches }),
    [book, lang, paper, daily, branches],
  );
  const tableChoices: ExportChoices = useMemo(() => ({ ...tables, lang }), [tables, lang]);

  useEffect(() => {
    if (!open || tab !== 'pdf') return;
    let live = true;
    const of = exportPath(trip, 'book', bookChoices);
    getBookPreview(trip, bookChoices).then(
      (book) => {
        if (live) setMade({ of, book });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [open, tab, trip, bookChoices]);

  useEffect(() => {
    if (!open || tab !== 'table') return;
    let live = true;
    getTableRows(trip, tableChoices).then(
      (read) => {
        if (live) setRows(read);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [open, tab, trip, tableChoices]);

  useEffect(() => {
    if (!open || tab !== 'ics') return;
    let live = true;
    getCalendarFeed(trip).then(
      (read) => {
        if (live) setLink(read);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [open, tab, trip]);

  const name = plan?.trip.name ?? '';
  const preview = made?.of === exportPath(trip, 'book', bookChoices) ? made.book : null;
  const others = family.filter((kin) => kin.id !== trip && kin.shared).map((kin) => kin.name);
  const sights =
    plan?.stops.flatMap((stop) => stop.agenda).filter((item) => item.place).length ?? 0;
  const makeFeed = () => {
    renewCalendarFeed(trip).then(
      (made) => {
        setLink(made);
        setCopied(false);
      },
      () => {
        setFailed(true);
      },
    );
  };

  const bar = (
    <div className="segmented export-tabs" role="tablist" aria-label={t('exportTrip.kinds')}>
      {tabs.map((one) => (
        <button
          key={one}
          type="button"
          role="tab"
          id={`export-tab-${one}`}
          aria-selected={tab === one}
          aria-controls="export-panel"
          onClick={() => {
            setTab(one);
          }}
        >
          {t(`exportTrip.${one}`)}
        </button>
      ))}
    </div>
  );

  const download = (() => {
    switch (tab) {
      case 'pdf':
        return { href: exportPath(trip, 'pdf', bookChoices), what: 'PDF' };
      case 'gpx':
        return { href: exportPath(trip, 'gpx', { ...gpx, lang }), what: 'GPX' };
      case 'geo':
        return {
          href: exportPath(trip, geoKind, {
            stops: geo.places,
            sights: geo.places,
            ways: geo.lines,
            flights: geo.lines,
            costs: geo.costs,
            lang,
          }),
          what: geoKind === 'geojson' ? 'GeoJSON' : geoKind.toUpperCase(),
        };
      case 'table':
        return {
          href: exportPath(trip, tableKind, tableChoices),
          what: tableKind === 'xlsx' ? 'Excel' : 'CSV',
        };
      case 'ics':
        return null;
    }
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('exportTrip.title', { name })}
      wide
      bar={bar}
    >
      <div
        className="dialog-body export-body"
        id="export-panel"
        role="tabpanel"
        aria-labelledby={`export-tab-${tab}`}
      >
        {failed && (
          <p className="form-failed" role="alert">
            {t('exportTrip.failed')}
          </p>
        )}
        {tab === 'pdf' && (
          <div className="export-cols">
            <div className="export-opts">
              <Group title={t('exportTrip.contents')}>
                {(['cover', 'days', 'bookings', 'entry', 'money'] as const).map((part) => (
                  <Check
                    key={part}
                    label={t(`exportTrip.${part}`)}
                    hint={t(`exportTrip.${part}Hint`)}
                    checked={book[part]}
                    onChange={(checked) => {
                      setBook({ ...book, [part]: checked });
                    }}
                  />
                ))}
              </Group>
              <Group title={t('exportTrip.format')}>
                <div className="export-row">
                  <div className="field">
                    <span className="field-label">{t('exportTrip.language')}</span>
                    <Choose
                      label={t('exportTrip.language')}
                      options={[
                        ['et', 'Eesti'],
                        ['en', 'English'],
                      ]}
                      value={lang}
                      onChange={setLang}
                    />
                  </div>
                  <div className="field">
                    <span className="field-label">{t('exportTrip.paper')}</span>
                    <Choose
                      label={t('exportTrip.paper')}
                      options={[
                        ['a4', 'A4'],
                        ['letter', 'Letter'],
                      ]}
                      value={paper}
                      onChange={setPaper}
                    />
                  </div>
                </div>
                <Check label={t('exportTrip.dayAPage')} checked={daily} onChange={setDaily} />
                {others.length > 0 && (
                  <Check
                    label={t('exportTrip.branches')}
                    hint={t('exportTrip.branchesHint', { names: others.join(', ') })}
                    checked={branches}
                    onChange={setBranches}
                  />
                )}
              </Group>
            </div>
            <div>
              {plan && <BookPages plan={plan} preview={preview} />}
              <p className="quiet export-note">
                {preview
                  ? t('exportTrip.pages', {
                      count: preview.pages,
                      size: sizeSaid(preview.bytes, i18n.language),
                    })
                  : t('exportTrip.counting')}{' '}
                {t('exportTrip.serverMade')}
              </p>
            </div>
          </div>
        )}
        {tab === 'gpx' && (
          <div className="export-opts">
            {(['stops', 'ways', 'flights', 'sights'] as const).map((part) => (
              <Check
                key={part}
                label={t(`exportTrip.${part}`)}
                hint={t(`exportTrip.${part}Hint`, { count: sights })}
                checked={gpx[part]}
                onChange={(checked) => {
                  setGpx({ ...gpx, [part]: checked });
                }}
              />
            ))}
            <p className="quiet export-note">
              {t('exportTrip.gpxFile', { file: `${fileName(name)}.gpx` })}
            </p>
          </div>
        )}
        {tab === 'geo' && (
          <div className="export-opts">
            <Group title={t('exportTrip.format')}>
              <Choose
                label={t('exportTrip.format')}
                options={[
                  ['kml', t('exportTrip.kml')],
                  ['kmz', t('exportTrip.kmz')],
                  ['geojson', t('exportTrip.geojson')],
                ]}
                value={geoKind}
                onChange={setGeoKind}
              />
            </Group>
            {(
              [
                ['places', 'places'],
                ['lines', 'lines'],
                ['costs', 'amounts'],
              ] as const
            ).map(([part, said]) => (
              <Check
                key={part}
                label={t(`exportTrip.${said}`)}
                hint={t(`exportTrip.${said}Hint`)}
                checked={geo[part]}
                onChange={(checked) => {
                  setGeo({ ...geo, [part]: checked });
                }}
              />
            ))}
            <p className="quiet export-note">{t('exportTrip.geoNote')}</p>
          </div>
        )}
        {tab === 'table' && (
          <div className="export-opts">
            <Group title={t('exportTrip.whatGoes')}>
              {(
                [
                  ['costs', 'costs'],
                  ['bookings', 'tableBookings'],
                  ['days', 'tableDays'],
                ] as const
              ).map(([part, said]) => (
                <Check
                  key={part}
                  label={t(`exportTrip.${said}`)}
                  hint={t(`exportTrip.${said}Hint`)}
                  checked={tables[part]}
                  onChange={(checked) => {
                    setTables({ ...tables, [part]: checked });
                  }}
                />
              ))}
            </Group>
            <Group title={t('exportTrip.format')}>
              <Choose
                label={t('exportTrip.format')}
                options={[
                  ['xlsx', t('exportTrip.xlsx')],
                  ['csv', t('exportTrip.csv')],
                ]}
                value={tableKind}
                onChange={setTableKind}
              />
              <p className="quiet export-note">{t('exportTrip.tableNote')}</p>
            </Group>
            <RowsPreview tables={rows} />
          </div>
        )}
        {tab === 'ics' && (
          <div className="export-opts">
            <p className="export-lead">{t('exportTrip.icsLead')}</p>
            <FeedField link={link} />
            <p className="quiet export-note">{t('exportTrip.feedNote')}</p>
            <div className="export-row">
              {link !== null && (
                <button type="button" className="link-button" onClick={makeFeed}>
                  {t('exportTrip.newFeed')}
                </button>
              )}
              <a
                className="link-button"
                href={`/_/api/trips/${encodeURIComponent(trip)}/calendar.ics`}
                download
              >
                {t('exportTrip.icsOnce')}
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={onClose}>
          {t('exportTrip.cancel')}
        </button>
        {download ? (
          <a className="button button-primary" href={download.href} download>
            {t('exportTrip.download', { what: download.what })}
          </a>
        ) : link !== null ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              navigator.clipboard.writeText(link).then(
                () => {
                  setCopied(true);
                },
                () => undefined,
              );
            }}
          >
            {t(copied ? 'exportTrip.copied' : 'exportTrip.copy')}
          </button>
        ) : (
          <button type="button" className="button button-primary" onClick={makeFeed}>
            {t('exportTrip.makeFeed')}
          </button>
        )}
      </div>
    </Dialog>
  );
}
